/**
 * AddToCollectionDialog - Dialog para adicionar artigo(s) a uma coleção
 *
 * Mostra apenas coleções do tipo 'articles' (coleções estáticas)
 * Coleções do tipo 'tags' são dinâmicas e não permitem adicionar artigos manualmente
 *
 * Permite criar nova coleção diretamente digitando o nome e pressionando Enter
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  FolderSpecial as CollectionIcon,
  Search as SearchIcon,
  Add as AddIcon,
  CreateNewFolder as CreateIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

const AddToCollectionDialog = ({
  open,
  onClose,
  articleIds = [], // Array de IDs de artigos para adicionar
  onSuccess // Callback após adicionar com sucesso
}) => {
  const theme = useTheme();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Buscar coleções do tipo 'articles' ao abrir o dialog
  useEffect(() => {
    if (open) {
      fetchCollections();
      setSelectedCollections([]);
      setSearchQuery('');
      setError(null);
    }
  }, [open]);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const res = await api.get('/collections');
      // Filtrar apenas coleções do tipo 'articles'
      const articleCollections = (res.data.data || []).filter(c => c.type === 'articles');
      setCollections(articleCollections);
    } catch (err) {
      console.error('Erro ao buscar coleções:', err);
      setError('Erro ao carregar coleções');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCollection = (collectionId) => {
    setSelectedCollections(prev => {
      if (prev.includes(collectionId)) {
        return prev.filter(id => id !== collectionId);
      }
      return [...prev, collectionId];
    });
  };

  // Criar nova coleção e selecioná-la automaticamente
  const handleCreateCollection = async () => {
    const name = searchQuery.trim();
    if (!name) return;

    // Verificar se já existe uma coleção com esse nome
    const exists = collections.some(
      c => c.name.toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      setError('Já existe uma coleção com esse nome');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const res = await api.post('/collections', {
        name,
        type: 'articles', // Sempre criar como tipo 'articles'
        description: ''
      });

      const newCollection = res.data.data;

      // Adicionar à lista e selecionar automaticamente
      setCollections(prev => [newCollection, ...prev]);
      setSelectedCollections(prev => [...prev, newCollection._id]);
      setSearchQuery(''); // Limpar busca
    } catch (err) {
      console.error('Erro ao criar coleção:', err);
      setError(err.response?.data?.message || 'Erro ao criar coleção');
    } finally {
      setCreating(false);
    }
  };

  // Handler para tecla Enter no campo de busca
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.trim() && canCreateNew) {
      e.preventDefault();
      handleCreateCollection();
    }
  };

  const handleSave = async () => {
    if (selectedCollections.length === 0) {
      setError('Selecione pelo menos uma coleção');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Adicionar artigos a cada coleção selecionada
      const results = await Promise.all(
        selectedCollections.map(collectionId =>
          api.post(`/collections/${collectionId}/articles`, { articleIds })
        )
      );

      // Contar quantos artigos foram adicionados
      const totalAdded = results.reduce((sum, res) => {
        const message = res.data.message || '';
        const match = message.match(/(\d+)/);
        return sum + (match ? parseInt(match[1]) : articleIds.length);
      }, 0);

      onSuccess?.({
        collectionsCount: selectedCollections.length,
        articlesCount: articleIds.length
      });
      onClose();
    } catch (err) {
      console.error('Erro ao adicionar às coleções:', err);
      setError(err.response?.data?.message || 'Erro ao adicionar às coleções');
    } finally {
      setSaving(false);
    }
  };

  // Filtrar coleções pela busca
  const filteredCollections = collections.filter(collection =>
    collection.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Verificar se pode criar nova coleção (nome não existe ainda)
  const canCreateNew = searchQuery.trim() && !collections.some(
    c => c.name.toLowerCase() === searchQuery.trim().toLowerCase()
  );

  const articleCount = articleIds.length;
  const articleLabel = articleCount === 1 ? '1 artigo' : `${articleCount} artigos`;

  return (
    <PlatformDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableScrollLock={true}
    >
      <PlatformDialogTitle
         title="Adicionar a Coleção"
         subtitle={`Adicionar ${articleLabel} às coleções selecionadas`}
         icon={<CollectionIcon />}
         onClose={onClose}
      />

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Campo de busca/criar */}
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar ou criar coleção..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={creating}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            )
          }}
          helperText={canCreateNew ? "Pressione Enter para criar" : null}
          sx={{ mb: 1 }}
        />

        {/* Botão para criar nova coleção */}
        {canCreateNew && (
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            startIcon={creating ? <CircularProgress size={16} /> : <CreateIcon />}
            onClick={handleCreateCollection}
            disabled={creating}
            sx={{ mb: 2, justifyContent: 'flex-start', textTransform: 'none' }}
          >
            {creating ? 'Criando...' : `Criar coleção "${searchQuery.trim()}"`}
          </Button>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : filteredCollections.length === 0 && !canCreateNew ? (
          <Box textAlign="center" py={4}>
            <CollectionIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {collections.length === 0
                ? 'Nenhuma coleção de artigos encontrada'
                : 'Nenhuma coleção corresponde à busca'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Digite um nome acima para criar uma nova coleção
            </Typography>
          </Box>
        ) : filteredCollections.length === 0 && canCreateNew ? (
          <Box textAlign="center" py={2}>
            <Typography variant="body2" color="text.secondary">
              Nenhuma coleção existente. Clique acima para criar "{searchQuery.trim()}"
            </Typography>
          </Box>
        ) : (
          <List sx={{ pt: 0 }}>
            {filteredCollections.map((collection) => {
              const isSelected = selectedCollections.includes(collection._id);
              return (
                <ListItem
                  key={collection._id}
                  disablePadding
                  secondaryAction={
                    <Chip
                      size="small"
                      label={`${collection.articleCount || 0} artigos`}
                      variant="outlined"
                    />
                  }
                >
                  <ListItemButton
                    onClick={() => handleToggleCollection(collection._id)}
                    selected={isSelected}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={collection.name}
                      secondary={collection.description || null}
                      primaryTypographyProps={{
                        fontWeight: isSelected ? 600 : 400
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
        <Button onClick={onClose} disabled={saving} variant="text">
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || selectedCollections.length === 0}
          startIcon={saving ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {saving ? 'Adicionando...' : `Adicionar a ${selectedCollections.length || ''} coleção${selectedCollections.length !== 1 ? 'ões' : ''}`}
        </Button>
      </DialogActions>
    </PlatformDialog>
  );
};

export default AddToCollectionDialog;

